// NEW FILE: src/components/SystemHealth.js
// Extract all system health functionality into its own component

import React, { useState, useEffect, useRef } from 'react';
import { 
  RefreshCw, 
  AlertCircle, 
  CheckCircle, 
  Clock,
  Wifi,
  WifiOff,
  Info
} from 'lucide-react';
import { fetchSystemHealth } from '../utils/api';
import { formatTime } from '../utils/helpers';

const SystemHealth = ({
  loading,
  loadingMetaCampaigns,
  isUpdating,
  metaApiError,
  cacheStatus,
  connectionMethod,
  wsConnected,
  wsStatus,
  connectionQuality,
  subscriptionCount,
  connectedClients,
  useWebSocketEnabled,
  onToggleWebSocket,
  onReconnectWebSocket,
  inMenu = false // New prop to indicate if rendered in hamburger menu
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [systemHealth, setSystemHealth] = useState(null);
  const [cacheStats, setCacheStats] = useState(null);
  const [appVersion, setAppVersion] = useState('Loading...');
  const healthCheckIntervalRef = useRef(null);

  // Fetch system health
  const checkSystemHealth = async () => {
    try {
      const { healthData, cacheData } = await fetchSystemHealth();
      console.log('🏥 Health check received:', {
        healthConnections: healthData?.websocket?.total_connections,
        cacheConnections: cacheData?.websocket?.active_connections,
        timestamp: new Date().toISOString()
      });
      setSystemHealth(healthData);
      setCacheStats(cacheData);
      
      // Update connected clients from health data
      if (healthData?.websocket?.total_connections !== undefined) {
        // We don't have access to setConnectedClients here, so we'll display it directly from healthData
      }
    } catch (err) {
      // Only log non-connection errors to reduce console noise
      if (!err.message?.includes('Failed to fetch')) {
        console.error('Error fetching system health:', err);
      }
      setSystemHealth({ status: 'error', error: err.message });
    }
  };

  // Fetch service worker version on mount - read from ACTIVE worker
  useEffect(() => {
    const fetchAppVersion = async () => {
      try {
        // Get the currently active service worker
        const registration = await navigator.serviceWorker.getRegistration('/');

        if (!registration || !registration.active) {
          console.warn('[SystemHealth] No active service worker found');
          setAppVersion('No SW');
          return;
        }

        const activeWorker = registration.active;

        // Fetch the active worker's script to get version
        const response = await fetch(`${activeWorker.scriptURL}?t=${Date.now()}`, {
          cache: 'no-store'
        });
        const text = await response.text();
        const versionMatch = text.match(/const VERSION = '([^']+)'/);

        if (versionMatch) {
          setAppVersion(`v${versionMatch[1]}`);
          console.log('[SystemHealth] Active service worker version:', versionMatch[1]);

          // Also check if there's a waiting worker with a newer version
          if (registration.waiting) {
            const waitingResponse = await fetch(`${registration.waiting.scriptURL}?t=${Date.now()}`, {
              cache: 'no-store'
            });
            const waitingText = await waitingResponse.text();
            const waitingVersionMatch = waitingText.match(/const VERSION = '([^']+)'/);
            if (waitingVersionMatch) {
              console.log('[SystemHealth] Waiting worker version:', waitingVersionMatch[1], '(not shown - click Update Now to activate)');
            }
          }
        } else {
          console.warn('[SystemHealth] Could not parse version from service worker');
          setAppVersion('Unknown');
        }
      } catch (err) {
        console.error('[SystemHealth] Failed to fetch app version:', err);
        setAppVersion('Unknown');
      }
    };

    fetchAppVersion();
  }, []);

  // Health check management:
  // - When WebSocket is connected: No polling needed, server pushes real-time updates
  // - When WebSocket is disconnected: Poll every 30 seconds for status
  // - Universe webhooks trigger instant updates via WebSocket, no need for polling
  useEffect(() => {
    // Always do initial check
    checkSystemHealth();
    
    // Clear any existing interval
    if (healthCheckIntervalRef.current) {
      clearInterval(healthCheckIntervalRef.current);
      healthCheckIntervalRef.current = null;
    }
    
    // Only poll if WebSocket is not connected
    // When WebSocket is connected, we get real-time updates
    if (!wsConnected) {
      console.log('🔄 Starting health check polling (WebSocket not connected)');
      healthCheckIntervalRef.current = setInterval(checkSystemHealth, 30000);
    } else {
      console.log('✅ WebSocket connected - disabling health check polling');
      // Do one final check to get the latest WebSocket stats
      checkSystemHealth();
    }
    
    // Expose force refresh function globally for debugging
    window.__forceHealthCheck = checkSystemHealth;
    
    return () => {
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
      }
      delete window.__forceHealthCheck;
    };
  }, [wsConnected]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDetails && !event.target.closest('[data-health-dropdown]')) {
        setShowDetails(false);
      }
    };

    if (showDetails) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDetails]);

  // Get overall status
  const getOverallStatus = () => {
    if (loading || loadingMetaCampaigns || isUpdating) {
      return {
        icon: RefreshCw,
        color: 'text-blue-600',
        label: 'Updating...',
        animate: true
      };
    }
    
    if (!systemHealth) {
      return {
        icon: Clock,
        color: 'text-gray-400',
        label: 'Checking...',
        animate: true
      };
    }
    
    if (systemHealth.status === 'error') {
      return {
        icon: AlertCircle,
        color: 'text-red-600',
        label: 'System Error'
      };
    }
    
    if (metaApiError) {
      return {
        icon: AlertCircle,
        color: 'text-orange-600',
        label: 'Partial Error'
      };
    }
    
    return {
      icon: CheckCircle,
      color: 'text-green-600',
      label: 'All Systems Go'
    };
  };

  const status = getOverallStatus();
  const StatusIcon = status.icon;

  return (
    <div className="relative" data-health-dropdown>
      <button
        onClick={() => setShowDetails(!showDetails)}
        className={inMenu
          ? "w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
          : "flex items-center gap-1 p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        }
        title="System Health"
      >
        <StatusIcon className={`w-4 h-4 ${status.color} ${status.animate ? 'animate-spin' : ''}`} />

        {inMenu ? (
          <span className="text-sm text-gray-700 dark:text-gray-300">
            System Health
          </span>
        ) : (
          <span className="text-xs text-gray-500 dark:text-gray-400 hidden lg:inline">
            {connectionMethod === 'hybrid' ? 'Hybrid' :
             connectionMethod === 'connecting' ? 'Connecting' :
             cacheStatus?.timestamp ? formatTime(new Date(cacheStatus.timestamp)) : 'Ready'}
          </span>
        )}
      </button>
      
      {/* Enhanced Health Details Dropdown */}
      {showDetails && systemHealth && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 z-50">
          <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                System Health
                <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-2">{appVersion}</span>
              </h3>
              <StatusIcon className={`w-4 h-4 ${status.color}`} />
            </div>
            
            {/* Connection Status */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Connection Status</h4>
              
              {/* WebSocket Client */}
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600 dark:text-gray-400">WebSocket Client</span>
                <div className="flex items-center gap-2">
                  <span className={`flex items-center gap-1 ${
                    wsConnected ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                  }`}>
                    {wsConnected ? (
                      <Wifi className="w-3 h-3" />
                    ) : (
                      <WifiOff className="w-3 h-3" />
                    )}
                    {wsConnected ? 'Connected' : wsStatus || 'Disconnected'}
                  </span>
                  {wsConnected && connectionQuality && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                      ({connectionQuality})
                    </span>
                  )}
                </div>
              </div>
              
              {/* Active Subscriptions */}
              {wsConnected && subscriptionCount > 0 && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">Active Subscriptions</span>
                  <span className="text-green-600 dark:text-green-400 font-medium">{subscriptionCount}</span>
                </div>
              )}
              
              {/* Connected Clients */}
              {(systemHealth?.websocket?.total_connections !== undefined || cacheStats?.websocket?.active_connections !== undefined) && (
                <>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-400">Connected Clients</span>
                    <span className="text-blue-600 dark:text-blue-400 font-medium">
                      {cacheStats?.websocket?.active_connections ?? systemHealth?.websocket?.total_connections ?? 0}
                    </span>
                  </div>
                  
                  {/* Client Details */}
                  <div className="ml-4 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span>Your session: {window._wsClientId ? window._wsClientId.slice(0, 8) + '...' : 'Unknown'}</span>
                    </div>
                    {/* Per-connection identity (verified at the ws upgrade and kept on each socket) */}
                    {Array.isArray(systemHealth?.websocket?.clients) && systemHealth.websocket.clients.length > 0 ? (
                      systemHealth.websocket.clients.map((c, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                          <span>{c.user}{c.connectedAt ? ` · since ${new Date(c.connectedAt).toLocaleTimeString()}` : ''}</span>
                        </div>
                      ))
                    ) : (
                      (cacheStats?.websocket?.active_connections ?? systemHealth?.websocket?.total_connections ?? 0) > 1 && (
                        <div className="text-xs italic">
                          +{(cacheStats?.websocket?.active_connections ?? systemHealth?.websocket?.total_connections ?? 1) - 1} other client{(cacheStats?.websocket?.active_connections ?? systemHealth?.websocket?.total_connections ?? 0) > 2 ? 's' : ''}
                        </div>
                      )
                    )}
                  </div>
                </>
              )}
              
              {/* WebSocket Subscriptions */}
              {systemHealth?.websocket?.active_subscriptions !== undefined && (
                <>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-400">Total Subscriptions</span>
                    <span className="text-purple-600 dark:text-purple-400 font-medium">{systemHealth.websocket.active_subscriptions}</span>
                  </div>
                  
                  {/* Subscription Breakdown */}
                  {systemHealth.websocket.active_subscriptions > 0 && subscriptionCount > 0 && (
                    <div className="ml-4 text-xs text-gray-500 dark:text-gray-400">
                      <div>Your subscriptions: {subscriptionCount}</div>
                      <div className="text-xs italic">
                        Avg per client: {Math.round(systemHealth.websocket.active_subscriptions / (cacheStats?.websocket?.active_connections ?? systemHealth?.websocket?.total_connections ?? 1))}
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {/* Proxy Server */}
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600 dark:text-gray-400">Proxy Server</span>
                <span className={`flex items-center gap-1 ${
                  systemHealth.status === 'ok' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${
                    systemHealth.status === 'ok' ? 'bg-green-600 dark:bg-green-400' : 'bg-red-600 dark:bg-red-400'
                  }`}></div>
                  {systemHealth.status === 'ok' ? 'Online' : 'Error'}
                </span>
              </div>
            </div>
            
            {/* Data Updates */}
            <div className="space-y-2 border-t dark:border-gray-700 pt-3">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Data Updates</h4>
              
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600 dark:text-gray-400">Update Method</span>
                <span className="text-gray-700 dark:text-gray-300">
                  {connectionMethod === 'hybrid' ? (
                    <span className="text-blue-600 dark:text-blue-400 font-medium">Hybrid Mode</span>
                  ) : connectionMethod === 'connecting' ? (
                    <span className="text-yellow-600 dark:text-yellow-400">Connecting...</span>
                  ) : connectionMethod === 'disabled' ? (
                    <span className="text-gray-500 dark:text-gray-400">WebSocket Disabled</span>
                  ) : (
                    <span className="text-gray-600 dark:text-gray-400">HTTP Only</span>
                  )}
                </span>
              </div>
              
              {connectionMethod === 'hybrid' && (
                <div className="text-xs text-gray-600 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
                  <div className="font-medium text-blue-700 dark:text-blue-300 mb-1">Hybrid Mode Active:</div>
                  <div>• Events list: HTTP (fast load)</div>
                  <div>• Event details: WebSocket (live)</div>
                  <div>• Campaigns: WebSocket (live)</div>
                  <div>• Update frequency: Every 30s</div>
                </div>
              )}
              
              {/* Cache Status */}
              {cacheStatus?.timestamp && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">Events List Cache</span>
                  <span className="text-gray-700 dark:text-gray-300">
                    {cacheStatus.status === 'fresh' ? '✅ Fresh' : 
                     cacheStatus.status === 'stale' ? '⚠️ Stale' : 
                     '📡 Live'} • {formatTime(new Date(cacheStatus.timestamp))}
                  </span>
                </div>
              )}
            </div>
            
            {/* Errors */}
            {(systemHealth.status === 'error' || metaApiError) && (
              <div className="space-y-2 border-t dark:border-gray-700 pt-3">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Issues</h4>
                
                {systemHealth.status === 'error' && (
                  <div className="bg-red-50 dark:bg-red-900/20 p-2 rounded">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-red-700 dark:text-red-300">
                          System Error
                        </div>
                        <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                          {systemHealth.error}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {metaApiError && (
                  <div className="bg-orange-50 dark:bg-orange-900/20 p-2 rounded">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-orange-700 dark:text-orange-300">
                          Meta API Error
                        </div>
                        <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                          {metaApiError}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Cache Stats */}
            {cacheStats && (
              <div className="space-y-2 border-t dark:border-gray-700 pt-3">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Cache Statistics</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Hit Rate:</span>
                    <span className="font-medium">{cacheStats.hitRate || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Size:</span>
                    <span className="font-medium">{cacheStats.size || 'N/A'}</span>
                  </div>
                </div>
                {cacheStats.stats && (
                  <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                    <div>Total: {cacheStats.stats.total.hits} hits / {cacheStats.stats.total.misses} misses</div>
                    {cacheStats.stats.breakdown && (
                      <div className="mt-1 pl-2">
                        <div>• List: {cacheStats.stats.breakdown.list.hits}h/{cacheStats.stats.breakdown.list.misses}m</div>
                        <div>• Detail: {cacheStats.stats.breakdown.detail.hits}h/{cacheStats.stats.breakdown.detail.misses}m</div>
                        <div>• Meta: {cacheStats.stats.breakdown.meta.hits}h/{cacheStats.stats.breakdown.meta.misses}m</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="border-t dark:border-gray-700 pt-3">
              <div className="flex gap-2 mb-2">
                <button
                  onClick={onToggleWebSocket}
                  className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                    useWebSocketEnabled 
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200' 
                      : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200'
                  }`}
                >
                  {useWebSocketEnabled ? 'Disable WebSocket' : 'Enable WebSocket'}
                </button>
                
                {wsConnected && (
                  <button
                    onClick={onReconnectWebSocket}
                    className="px-3 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-sm font-medium hover:bg-blue-200 transition-colors"
                  >
                    Reconnect
                  </button>
                )}
              </div>
              
              <button
                onClick={checkSystemHealth}
                className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Refresh Health Check
              </button>
            </div>
            
            {/* Client Info */}
            {systemHealth?.websocket?.total_connections > 0 && (
              <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded">
                <div className="text-xs">
                  <div className="font-medium text-gray-700 dark:text-gray-300 mb-2">Client Information:</div>
                  <div className="space-y-1 text-gray-600 dark:text-gray-400">
                    <div>• Each client represents a browser tab/window</div>
                    <div>• Multiple tabs share the same session</div>
                    <div>• {systemHealth.websocket.total_connections} active connection{systemHealth.websocket.total_connections > 1 ? 's' : ''} means {systemHealth.websocket.total_connections} open dashboard{systemHealth.websocket.total_connections > 1 ? 's' : ''}</div>
                    <div>• Each client can subscribe to multiple events</div>
                    <div className="mt-2 text-xs italic text-gray-500 dark:text-gray-400">
                      Note: Client IP addresses are not exposed by the WebSocket API for privacy reasons
                    </div>
                    {systemHealth.websocket.total_connections > 2 && (
                      <div className="mt-2 text-yellow-600 dark:text-yellow-400">
                        ⚠️ High client count may impact performance
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Info */}
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-700 dark:text-blue-300">
                  <div className="font-medium mb-1">System Overview:</div>
                  <div>• WebSocket: Real-time data streaming</div>
                  <div>• Proxy: API rate limit protection</div>
                  <div>• Cache: Fast event list loading</div>
                  <div>• Hybrid: Best of both worlds</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemHealth;